import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";

import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { BookableAssetsGridView } from "@/components/BookableAssetsGridView/BookableAssetsGridView";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import type { TabItem } from "@/design-system/Tabs";
import { Tabs } from "@/design-system/Tabs";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { ServicesGridView } from "@/modules/service-partners/components/ServicesGridView";
import { ServicesListAction } from "@/modules/service-partners/components/ServicesListAction";
import { canViewServicePartners } from "@/modules/service-partners/permissions";
import { useBookableAssetsQueries } from "@/queries/bookable-assets/queryOptions";
import { useServiceQueries } from "@/queries/services/queryOptions";
import { useTranslation } from "@/translations";

enum ServiceTab {
  Services = "services",
  Assets = "assets",
}

// Admins always only see services, no tab options are provided
// Non-admins can see both services and assets when they have the right permissions
export function ServicePartnerList(): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const projectId = useProjectId();
  const hasPermission = usePermission();

  let defaultActiveTab: ServiceTab = ServiceTab.Services;
  if (!hasPermission(canViewServicePartners)) {
    defaultActiveTab = ServiceTab.Assets;
  }
  const [activeTab, setActiveTab] = useState<ServiceTab>(defaultActiveTab);

  const servicePartnerQueries = useServiceQueries();
  const bookableAssetQueries = useBookableAssetsQueries();
  const {
    data: servicesData,
    isError: isErrorServices,
    error: servicesError,
    isFetching: isFetchingServices,
    isFetchingNextPage: isFetchingMoreServices,
    hasNextPage: hasMoreServices,
    fetchNextPage: fetchMoreServices,
  } = useInfiniteQuery(servicePartnerQueries.listInfinite());
  const {
    data: assetsData,
    isError: isErrorAssets,
    error: assetsError,
    isFetching: isFetchingAssets,
    hasNextPage: hasMoreAssets,
    fetchNextPage: fetchMoreAssets,
    isFetchingNextPage: isFetchingMoreAssets,
  } = useInfiniteQuery(
    bookableAssetQueries.listInfinite({
      query: {
        HideUnpublished: !hasPermission((x) => x.assets.canManageAll || x.assets.canManageOwn),
      },
    }),
  );

  if ((isFetchingAssets && !isFetchingMoreAssets) || (isFetchingServices && !isFetchingMoreServices)) {
    return <FullSizeLoader />;
  }

  if (isErrorServices || isErrorAssets) {
    return <ErrorPage error={servicesError || assetsError} />;
  }

  const services = servicesData?.pages.flatMap((page) => page.items) ?? [];
  const assets = assetsData?.pages.flatMap((page) => page.items) ?? [];

  const tabItems: TabItem<ServiceTab>[] = [
    {
      label: t("common.entity.service.plural"),
      value: ServiceTab.Services,
      count: services.length,
      isHidden: !hasPermission(canViewServicePartners),
    },
    {
      label: t("common.entity.asset.plural"),
      value: ServiceTab.Assets,
      count: assets.length,
      isHidden: assets.length === 0,
    },
  ];

  const isAnyTabVisible = tabItems.some((tabItem) => !tabItem.isHidden);
  const isTabMenuVisible = !sessionUser.isAdmin && isAnyTabVisible;
  const isServicesVisible = hasPermission(canViewServicePartners) && sessionUser.isAdmin;

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.service.list.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.service.list.title"),
            },
          ]}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{ projectIds: [projectId] }}
            context={{ scope: "project", entity: "service-partners", page: "list" }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.service-partners.suggestion.1"),
              t("component.ask-nobi-chat-drawer.service-partners.suggestion.2"),
            ]}
          />
          {(sessionUser.isAdmin || activeTab === ServiceTab.Services) && <ServicesListAction entityType="service" />}
          {activeTab === ServiceTab.Assets && <ServicesListAction entityType="asset" />}
        </>
      }
    >
      <Stack gap="md">
        {/* Both Services & Assets */}
        {isTabMenuVisible && (
          <Tabs.Root tabs={tabItems} activeTab={activeTab} onSelectTab={(tab) => setActiveTab(tab)}>
            <Tabs.Content value={ServiceTab.Services}>
              <ServicesGridView
                onLoadMore={fetchMoreServices}
                isLoading={isFetchingMoreServices || isFetchingServices}
                hasMore={hasMoreServices}
                {...{ services }}
              />
            </Tabs.Content>
            <Tabs.Content value={ServiceTab.Assets}>
              <BookableAssetsGridView
                onLoadMore={fetchMoreAssets}
                isLoading={isFetchingMoreAssets || isFetchingAssets}
                hasMore={hasMoreAssets}
                {...{ assets }}
              />
            </Tabs.Content>
          </Tabs.Root>
        )}

        {/* or just Services */}
        {isServicesVisible && (
          <ServicesGridView
            onLoadMore={fetchMoreServices}
            isLoading={isFetchingMoreServices || isFetchingServices}
            hasMore={hasMoreServices}
            {...{ services }}
          />
        )}
      </Stack>
    </DocumentPage>
  );
}
